import { defineComponent, reactive, computed, provide, watch } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { Calendar } from '@hems/component';
import { PricingTable } from '@hems/container/src/forms/device/settings/_shared/pricing/component';
import { Helper } from '@hems/util';
import { ENV_LOCALE, GEN_TYPE, PROVIDE_INJECT_KEYS } from '@hems/util/src/constant';
export default defineComponent({
    name: 'PricingSetting',
    components: {
        PricingCalendarRange: Calendar.PricingCalendarRange,
        PricingTable,
    },
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        editable: {
            type: Boolean,
        },
    },
    emits: ['updatePricingSettings'],
    setup(props, { emit }) {
        const store = useStore();
        const langCd = computed(() => store.state.appCtx.langCd);
        const format = computed(() => {
            langCd;
            return getDateFormat(langCd.value);
        });
        const emsVersion = store.state.device.installation?.ems_ver;
        const locale = Helper.getEnvLocale();
        const state = reactive({
            data: props.modelValue,
            productModelNm: store.state.device.baseDeviceInfo?.product_model_nm ?? '',
            minDate: new Date(),
            duration: {
                start: null,
                end: null,
            },
            weekdays: [],
            weekend: [],
            isPermanent: false,
            isEmsVer25OrHigher: locale === ENV_LOCALE.US || Helper.checkValidEmsVersion(GEN_TYPE.GEN3, emsVersion, 25) === true,
        });
        const isEditable = computed(() => props.editable);
        const maxCount = computed(() => (state.isEmsVer25OrHigher ? 30 : 6));
        const minuteOptionUnit = computed(() => (state.isEmsVer25OrHigher ? 1 : 30));
        function init() {
            if (state.data) {
                state.duration.start = state.data.tou_start_date
                    ? moment(state.data.tou_start_date, 'YYYYMMDD').toDate()
                    : null;
                state.duration.end = state.data.tou_end_date
                    ? state.data.tou_end_date?.startsWith('9999')
                        ? null
                        : moment(state.data.tou_end_date, 'YYYYMMDD').toDate()
                    : null;
                state.weekdays = state.data.tou_weekday_section;
                state.weekend = state.data.tou_weekend_section;
                state.isPermanent = state.data.tou_end_date?.startsWith('9999');
            }
        }
        function getParam() {
            const param = {
                tou_start_date: moment(state.duration.start).format('YYYYMMDD'),
                tou_end_date: state.isPermanent
                    ? moment(new Date('9999-01-01')).format('YYYYMMDD')
                    : moment(state.duration.end).format('YYYYMMDD'),
            };
            param.tou_weekday_count = state.weekdays?.length;
            param.tou_weekday_section = state.weekdays?.map((item) => ({
                time: item.time,
                action: item.action,
                inverterReference: item.inverterReference ? item.inverterReference.toString() : '0',
            }));
            param.tou_weekend_count = state.weekend?.length;
            param.tou_weekend_section = state.weekend?.map((item) => ({
                time: item.time,
                action: item.action,
                inverterReference: item.inverterReference ? item.inverterReference.toString() : '0',
            }));
            return param;
        }
        function getDateFormat(langCd) {
            switch (langCd) {
                case 'ko':
                    return 'YYYY.MM.DD';
                case 'de':
                    return 'DD.MM.YYYY';
                default:
                    return 'DD/MM/YYYY';
            }
        }
        function applyPermanent(isPermanent) {
            state.isPermanent = isPermanent;
        }
        watch(() => [state.duration, state.isPermanent, state.weekdays, state.weekend], () => {
            emit('updatePricingSettings', getParam());
        }, { deep: true });
        init();
        const maxInverterRef = Helper.extractMaxInvRefFromProductModel(state.productModelNm);
        provide(PROVIDE_INJECT_KEYS.MAX_INVERTER_REF, maxInverterRef);
        return {
            state,
            langCd,
            format,
            maxCount,
            minuteOptionUnit,
            applyPermanent,
            isEditable,
        };
    },
});
